import { maskVendor } from "@/shared/lib/vendorMask";
import { logger } from "@/shared/lib/logger";
import { reportIncident } from "@/shared/lib/telemetry";
import { reportApiServerError } from "@/shared/lib/errorReporting";
import { generateTraceId } from "@/shared/lib/trace";
import { PAYLOAD_TOO_LARGE_MESSAGE } from "@/shared/lib/uploadLimits";
import { LICENSE_READONLY_CODE, markLicenseReadonly } from "@/shared/lib/licenseReadonly";
import {
  PASSWORD_CHANGE_REQUIRED_CODE,
  markPasswordChangeRequired,
} from "@/shared/lib/passwordChangeRequired";
import { NetworkError, RequestTimeoutError, timeoutMsFor } from "./requestTimeout";
import { adoptRenewedToken } from "./sessionRenewal";

const BASE = "/api/v1";
const TOKEN_KEY = "xbgeo-token";

/**
 * V26 (2026-05-14) Wave 4.6 — API 错误包含 errorCode,caller 可按 code 分类提示。
 * 旧 caller 沿用 catch (Error) 仍 catch 到(ApiError extends Error)。新 caller 用
 * instanceof ApiError 取出 errorCode 走 switch 分类。
 */
export class ApiError extends Error {
  readonly errorCode: string;
  readonly httpStatus: number;
  readonly details: string[];
  /**
   * 本次请求的 X-Trace-ID(row-111,0811 学良「保存失败」无排查号):
   * 用户报障只有一句话术时,后端日志无从查起 —— toast 层统一把它亮给用户抄。
   */
  readonly traceId?: string;

  constructor(
    message: string,
    errorCode: string,
    httpStatus: number,
    details: string[] = [],
    traceId?: string
  ) {
    super(message);
    this.name = "ApiError";
    this.errorCode = errorCode;
    this.httpStatus = httpStatus;
    this.details = details;
    this.traceId = traceId;
  }
}

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

export function clearToken(): void {
  localStorage.removeItem(TOKEN_KEY);
}

/**
 * BP-05:401 的**唯一**处理口。此前 JSON / getBlob / sendBlob 各抄一份(三份拷贝 =
 * 改一处漏两处,blob 那两份连 SESSION_KICKED 都不认),现在收成这一处。
 *
 * ★核心改动:**别在第一次 401 就把票扔了**。实测票 ttl=24h、被踢时还剩 23h,
 * 前端却一次 401 就清 5 个 key 跳登录,用户重登约 8 次撞登录频控 429 —— 连登都登不回去。
 * 间歇性 401 不等于「你被登出了」。一次重试成本极低,能挡掉绝大多数瞬时抖动。
 *
 * ★边界:触发 401 的**上游原因未查明**(已排除 JWT 过期/换 secret/被挤下线/请求次数/特定页面),
 * 本函数**不对根因下结论** —— 但无论 401 从哪来,「一次失败就扔票」都是前端自己的设计缺失。
 */
const RETRY_DELAY_MS = 400;
const KICKED_KEY = "xbgeo-session-kicked";
// 踢出原因(B2 #6,0927):api SESSION_KICKED 响应带 reason(account_disabled / tenant_suspended /
// password_reset / logged_in_elsewhere / membership_changed),LoginPage 按它分句;没有就中性句。
// key 与 features/auth/model/kickedReason 共用同一个字面量(shared 层不反向 import feature)。
const KICKED_REASON_KEY = "xbgeo-session-kicked-reason";
// key 与 features/auth/model/returnTo 共用同一个字面量(shared 层不反向 import feature)
const RETURN_TO_KEY = "xbgeo-return-to";

/** 判 SESSION_KICKED(票已作废,不该重试)+ 取出踢出原因(顶层 reason 或 data.reason,都没有 → null)。 */
async function readSessionKicked(
  res: Response
): Promise<{ kicked: boolean; reason: string | null }> {
  try {
    const body = (await res.clone().json()) as {
      code?: string;
      reason?: unknown;
      data?: { reason?: unknown } | null;
    };
    if (body?.code !== "SESSION_KICKED") return { kicked: false, reason: null };
    const raw = body.reason ?? body.data?.reason;
    return { kicked: true, reason: typeof raw === "string" && raw.trim() ? raw.trim() : null };
  } catch {
    return { kicked: false, reason: null }; // 非 JSON(blob 端点常见)→ 按普通 401 走重试
  }
}

/** 清票 + 记下来处 + 跳登录。 */
function logoutAndRedirect(kicked: boolean, reason: string | null = null): never {
  if (kicked) {
    sessionStorage.setItem(KICKED_KEY, "1");
    if (reason) sessionStorage.setItem(KICKED_REASON_KEY, reason);
    else sessionStorage.removeItem(KICKED_REASON_KEY);
  }
  // 1030:先记下返回地址的主人(换账号登录不跳进别人的页面,见 returnTo.RETURN_TO_OWNER_KEY)
  let owner: string | null = null;
  try {
    const raw = localStorage.getItem("xbgeo-auth");
    owner = raw ? String((JSON.parse(raw) as { userId?: number }).userId ?? "") || null : null;
  } catch {
    owner = null;
  }
  clearToken();
  localStorage.removeItem("xbgeo-auth");
  localStorage.removeItem("xbgeo-permissions");
  // 代客模式残留:代客 JWT 过期触发 401 时,原始 PLATFORM_ADMIN token 仍留在 localStorage(M8)
  localStorage.removeItem("xbgeo-origin-token");
  localStorage.removeItem("xbgeo-impersonation");
  // ★硬跳转会丢 React Router state,而 LoginPage 读的是 state.from.pathname ——
  // 用 sessionStorage 递来处(与既有 xbgeo-session-kicked 同范式)。
  // 已经在登录页就不记,否则登录后自己跳自己。
  try {
    const here = `${window.location.pathname}${window.location.search || ""}`;
    if (window.location.pathname && !window.location.pathname.startsWith("/login")) {
      sessionStorage.setItem(RETURN_TO_KEY, here);
      if (owner) sessionStorage.setItem("xbgeo-return-to-owner", owner);
      else sessionStorage.removeItem("xbgeo-return-to-owner");
    }
  } catch {
    /* sessionStorage 不可用(隐私模式)时不阻断登出 */
  }
  window.location.replace("/login");
  throw new Error(kicked ? "SessionKicked" : "Unauthorized");
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

/**
 * 边缘 nginx 拦下的上传(HTTP 413)—— 给一句人话,而不是一个状态码。
 *
 * ★为什么要单独一支:超过 `client_max_body_size` 时 **nginx 直接回一张 HTML 错误页**,
 *   请求根本到不了 backend(后端日志里连一行都没有)。我们的信封解析在这里必然抛,
 *   老代码落到「非 JSON」兜底,用户只看到「服务异常 (413)」/「请求失败 (413)」——
 *   0921 测试机真发生 3 次(poster/uploads 1 次、tenant/knowledge/upload 2 次),
 *   用户侧的呈现就是一句没有原因的「上传失败」。
 *
 * ★★**必须在 JSON 解析失败之后才判**:后端自己也会发 413(如 POSTER_UPLOAD_TOO_LARGE
 *   「上传文件超过 10MB」),那一条是带信封的,而且**它的数字才是那条路的真瓶颈**。
 *   若在解析前就按状态码劫持,就会拿 nginx 的 100MB 去盖掉后端的 10MB —— 比不报还糟。
 */
function payloadTooLargeError(path: string, traceId: string): ApiError {
  logger.error("API payload too large (nginx 413, body is HTML)", { path, traceId });
  return new ApiError(PAYLOAD_TOO_LARGE_MESSAGE, "PAYLOAD_TOO_LARGE", 413, [], traceId);
}

/**
 * 网关层(nginx)回的 HTML 502/503/504 —— backend 重启中 / 挂了 / 处理超时(0926 修复波 E)。
 *
 * ★此前落「服务异常 (502)」:只有状态码没有原因,且 504 时用户不知道操作其实可能还在后台跑,
 *   重试 = 再付一次钱。给 ApiError + 客户端码(GATEWAY_ERROR / GATEWAY_TIMEOUT),公共错误层按码给人话;
 *   httpStatus 保留真实状态码(useDiscoverRun 等按 502/504 判「被网关掐断、后台仍在跑」的地方由此生效)。
 * 只收这三个状态码,其它非 JSON 响应仍走原来的兜底。
 */
function gatewayError(status: number, traceId: string): ApiError | null {
  if (status === 504) {
    return new ApiError(
      "服务器处理超时,操作可能仍在后台进行,请稍后刷新看看再决定是否重试",
      "GATEWAY_TIMEOUT",
      504,
      [],
      traceId
    );
  }
  if (status === 502 || status === 503) {
    return new ApiError(
      "服务器暂时连不上(可能正在更新),请稍后重试",
      "GATEWAY_ERROR",
      status,
      [],
      traceId
    );
  }
  return null;
}

/**
 * 非 JSON 响应 → 人话 ApiError(0926 修复波第三批:给**不走本客户端**的另两套 fetch 用 ——
 * wecom-h5 的 `wecomApi.request` 与语音陪练外链页 `VoicePracticePage.req`,它们此前抛
 * 「请求失败 (502)」这种只有状态码的话,H5 直接把 e.message 显示给用户)。
 *
 * 413 / 502·503·504 与本文件主路同口径(复用上面两支);其它状态给一句通用人话,
 * 状态码留在 httpStatus 上,不进 message。
 */
export function nonJsonResponseError(status: number, path: string, traceId = ""): ApiError {
  if (status === 413) return payloadTooLargeError(path, traceId);
  const gateway = gatewayError(status, traceId);
  if (gateway) return gateway;
  return new ApiError("服务暂时出了点问题,请稍后重试", "UNKNOWN", status, [], traceId || undefined);
}

/**
 * ADV-LOCK-1:带 AbortController 超时的 fetch —— 本仓**唯一**的出网口都过这里。
 *
 * 超时抛 {@link RequestTimeoutError}(可 catch 的普通错误)→ 调用方既有的 catch/finally
 * 原路生效,UI 自己就解锁了;同时落一条埋点(下次再发生有证据,见 telemetry 头注)。
 * 每次尝试新建 controller:abort 过的 signal 不能复用(401 重试会二次发请求)。
 */
async function fetchWithTimeout(
  url: string,
  init: RequestInit,
  timeoutMs: number,
  meta: { path: string; traceId: string }
): Promise<Response> {
  const controller = new AbortController();
  const startedAt = Date.now();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(url, { ...init, signal: controller.signal });
  } catch (e) {
    if (controller.signal.aborted) {
      const elapsedMs = Date.now() - startedAt;
      reportIncident({
        event: "request_timeout",
        path: meta.path,
        elapsedMs,
        traceId: meta.traceId,
      });
      throw new RequestTimeoutError(meta.path, elapsedMs, meta.traceId);
    }
    // 断网 / 连接被重置:fetch 抛 TypeError(「Failed to fetch」/「Load failed」,浏览器各说各的英文)。
    // 换成带人话的 NetworkError(0926 修复波 E),其余异常原样抛。
    if (e instanceof TypeError) throw new NetworkError(meta.path, meta.traceId);
    throw e;
  } finally {
    clearTimeout(timer);
  }
}

/**
 * 收到 401 时的统一决策:返回 true = 调用方应**用同一张票重试一次**;
 * 否则本函数直接登出并抛(never 返回)。
 */
async function handleUnauthorized(res: Response, alreadyRetried: boolean): Promise<boolean> {
  const { kicked, reason } = await readSessionKicked(res);
  if (!kicked && !alreadyRetried) {
    await sleep(RETRY_DELAY_MS);
    return true;
  }
  logoutAndRedirect(kicked, reason);
}

/** 登录口:/auth/login · /auth/tenant/login · /auth/partner/login · /auth/unified/login。 */
export function isLoginPath(path: string): boolean {
  const p = path.split("?")[0];
  return p === "/auth/login" || /^\/auth\/(tenant|partner|unified)\/login$/.test(p);
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const token = getToken();
  const traceId = generateTraceId();

  const headers = new Headers(options.headers || undefined);
  if (!headers.has("Content-Type") && !(options.body instanceof FormData)) {
    headers.set("Content-Type", "application/json");
  }
  if (token) headers.set("Authorization", `Bearer ${token}`);
  headers.set("X-Trace-ID", traceId);

  // credentials:include — 企微 advisor-h5 走 HttpOnly cookie(wecom_member_token)鉴权,需同源带 cookie;
  // 管理后台 Bearer 流无害(JwtFilter 优先认 Authorization header)。
  // 登录路径自身的 401 不跳转 — 用户已经在登录页,而且 AuthContext AUTO 模式
  // 需要捕获 401 后 fallback 到另一个端点。其他路径 401 视为 session 过期 → 重试/跳登录。
  // 1004:四个登录口(平台 / 租户 / 渠道 / 统一)都算 —— 此前漏了渠道口,渠道管理员输错密码会被当成「会话过期」
  // 先等 1 秒重试一次(多吃一次登录频控)再整页跳登录。
  const isAuthPath = isLoginPath(path);

  const timeoutMs = timeoutMsFor(path, options.body);
  const send = () =>
    fetchWithTimeout(`${BASE}${path}`, { ...options, headers, credentials: "include" }, timeoutMs, {
      path,
      traceId,
    });
  let res = await send();
  if (res.status === 401 && !isAuthPath) {
    if (await handleUnauthorized(res, false)) {
      res = await send();
      if (res.status === 401) await handleUnauthorized(res, true);
    }
  }
  // 1005 滑动续期:活跃会话的响应头带同会话新票 → 换进本地(规则见 sessionRenewal)。
  adoptRenewedToken(res);

  let json: Record<string, unknown>;
  try {
    json = await res.json();
  } catch {
    // 解析失败**之后**才轮到 413:后端自己带信封的 413 已在上面被正常解出,不会走到这里。
    if (res.status === 413) throw payloadTooLargeError(path, traceId);
    logger.error("API non-JSON response", { path, status: res.status, traceId });
    // 可观测性(契约 §4):只报 5xx(网关 HTML 502/503/504 也在这里),4xx 不报;<500 内部直接忽略。
    reportApiServerError({ path, status: res.status, method: options.method, traceId });
    // 0926 修复波 P13:主路也走 nonJsonResponseError(413/网关同口径已在里面),不再抛只有
    // 状态码的「服务异常 (N)」;状态码留在 httpStatus 上、排查号照带。
    throw nonJsonResponseError(res.status, path, traceId);
  }
  if (!json.success) {
    const msg = (json.message as string) || "请求失败";
    const errorCode = (json.code as string) || (json.errorCode as string) || "UNKNOWN";
    // 可观测性(契约 §4):后端带信封的 5xx。只带路径模板 / 状态码 / 错误码 / 排查号,不带 message 正文。
    reportApiServerError({ path, status: res.status, method: options.method, errorCode, traceId });
    const details = Array.isArray(json.details) ? (json.details as string[]) : [];
    // 登录口的 4xx(密码错 / 去专属地址 / 账号停用)是给用户看的业务结果,不是故障:记 warn,不往控制台刷 error。
    (isAuthPath && res.status < 500 ? logger.warn : logger.error)("API error", {
      path,
      status: res.status,
      traceId,
      message: msg,
      errorCode,
    });
    // OEM W5 私有化:授权只读闸(423)→ 全站顶部横幅(widgets/LicenseReadonlyBanner)。
    if (errorCode === LICENSE_READONLY_CODE) markLicenseReadonly(true);
    // 0930:待改密会话撞了改密 / 登出 / 读 /me 之外的口 → 全站切到改密页(ProtectedRoute 读这个信号)。
    if (errorCode === PASSWORD_CHANGE_REQUIRED_CODE) markPasswordChangeRequired(true);
    // ★OEM 0929(C-C):后端 message / details 常带上游厂商名(「DashScope 返回 400」)——
    //   在 ApiError 构造处统一脱敏,全站 `e.message` 直出的几十处一次收口;日志上面已记原文。
    //   超管非代客态 vendorMask reveal 时原样。
    throw new ApiError(
      maskVendor(msg),
      errorCode,
      res.status,
      details.map((d) => maskVendor(d)),
      traceId
    );
  }
  return json.data as T;
}

export type RequestExtras = { headers?: Record<string, string> };

export const api = {
  get: <T>(path: string, extras?: RequestExtras) => request<T>(path, { headers: extras?.headers }),
  post: <T>(path: string, body: unknown, extras?: RequestExtras) =>
    request<T>(path, {
      method: "POST",
      body:
        body === null || body === undefined
          ? undefined
          : body instanceof FormData
            ? body
            : JSON.stringify(body),
      headers: extras?.headers,
    }),
  put: <T>(path: string, body: unknown, extras?: RequestExtras) =>
    request<T>(path, {
      method: "PUT",
      body: body instanceof FormData ? body : JSON.stringify(body),
      headers: extras?.headers,
    }),
  patch: <T>(path: string, body?: unknown, extras?: RequestExtras) =>
    request<T>(path, {
      method: "PATCH",
      body:
        body !== undefined ? (body instanceof FormData ? body : JSON.stringify(body)) : undefined,
      headers: extras?.headers,
    }),
  delete: <T>(path: string, extras?: RequestExtras) =>
    request<T>(path, { method: "DELETE", headers: extras?.headers }),
  /**
   * GET a binary response (PDF/CSV/Blob). 走同样的 Authorization + Trace + 401 跳登录拦截,
   * 但不解 JSON,直接返回原始 Response 给 caller 自行 .blob() / .headers 处理。
   * 用于 CSV / 报告下载这种需要 Content-Disposition 文件名的端点。
   */
  getBlob: async (path: string, extras?: RequestExtras): Promise<Response> => {
    const token = getToken();
    const traceId = generateTraceId();
    const headers = new Headers(extras?.headers || undefined);
    if (token) headers.set("Authorization", `Bearer ${token}`);
    headers.set("X-Trace-ID", traceId);
    const getOnce = () =>
      fetchWithTimeout(
        `${BASE}${path}`,
        { method: "GET", headers, credentials: "include" },
        timeoutMsFor(path),
        { path, traceId }
      );
    let res = await getOnce();
    if (res.status === 401 && (await handleUnauthorized(res, false))) {
      res = await getOnce();
      if (res.status === 401) await handleUnauthorized(res, true);
    }
    adoptRenewedToken(res);
    if (!res.ok) {
      logger.error("API blob non-OK", { path, status: res.status, traceId });
      reportApiServerError({ path, status: res.status, method: "GET", traceId });
      const gateway = gatewayError(res.status, traceId);
      if (gateway) throw gateway;
      throw new Error(`下载失败 (${res.status})`);
    }
    return res;
  },
  /**
   * Phase D (2026-05-16) — POST / PUT 端点返回二进制(JPEG/PNG)的统一入口。
   *
   * 海报详情页 3 个新端点用:
   *   - POST /poster/assets/{id}/download        (body=null,返 PNG attachment)
   *   - POST /poster/assets/{id}/preview-overlay (body=JSON {overlay,filter},返 JPEG bytes 实时预览)
   *
   * 跟普通 api.post 的差别:不解 JSON envelope,直接返 Response 给 caller .blob()。
   * 401 / 非 2xx 仍走统一拦截。
   */
  sendBlob: async (
    method: "POST" | "PUT",
    path: string,
    body?: unknown,
    extras?: RequestExtras
  ): Promise<Response> => {
    const token = getToken();
    const traceId = generateTraceId();
    const headers = new Headers(extras?.headers || undefined);
    if (token) headers.set("Authorization", `Bearer ${token}`);
    headers.set("X-Trace-ID", traceId);
    let serialized: BodyInit | undefined;
    if (body !== undefined && body !== null) {
      if (body instanceof FormData) {
        serialized = body;
      } else {
        if (!headers.has("Content-Type")) headers.set("Content-Type", "application/json");
        serialized = JSON.stringify(body);
      }
    }
    const send = () =>
      fetchWithTimeout(
        `${BASE}${path}`,
        { method, headers, body: serialized, credentials: "include" },
        timeoutMsFor(path, serialized),
        { path, traceId }
      );
    let res = await send();
    if (res.status === 401 && (await handleUnauthorized(res, false))) {
      res = await send();
      if (res.status === 401) await handleUnauthorized(res, true);
    }
    adoptRenewedToken(res);
    if (!res.ok) {
      // sendBlob 也是上传口(POST/PUT 带 FormData),同样会撞 nginx 的 413 —— 只修 JSON 那一支就漏了这条。
      if (res.status === 413) throw payloadTooLargeError(path, traceId);
      if (res.status === 423) markLicenseReadonly(true);
      logger.error("API blob non-OK", { path, status: res.status, traceId, method });
      reportApiServerError({ path, status: res.status, method, traceId });
      const gateway = gatewayError(res.status, traceId);
      if (gateway) throw gateway;
      throw new Error(`请求失败 (${res.status})`);
    }
    return res;
  },
};
